import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { PageHeader, DataTable, StatusPill, Section, Loading, KPITile } from "../components/Common";
import { useAuth } from "../lib/auth";

export default function Finance() {
  const { tenant } = useAuth();
  const [summary, setSummary] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [status, setStatus] = useState("");
  const term = tenant?.current_term || "Term";
  const ay = tenant?.academic_year || "";

  useEffect(() => {
    api.financeSummary().then(({ data }) => setSummary(data));
    api.invoices().then(({ data }) => setInvoices(data));
  }, []);

  const filtered = invoices.filter((i) => (status ? i.status === status : true));

  if (!summary) return <Loading />;

  const symbol = summary.currency_symbol || "$";
  const money = (v) => `${symbol}${Math.round(Number(v || 0)).toLocaleString()}`;

  return (
    <div data-testid="finance-page">
      <PageHeader kicker="Finance & Bursar" title="Fee collection & parent accounts" subtitle={`Tenant-scoped billing · ${ay} · ${term}.`} />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <KPITile testId="fin-billed" tone="dark" label="Total Billed" value={money(summary.billed)} />
        <KPITile testId="fin-collected" label="Collected" value={money(summary.collected)} accent={`${summary.collection_pct}%`} />
        <KPITile testId="fin-outstanding" label="Outstanding" value={money(summary.outstanding)} sub={`${summary.overdue_count} overdue`} />
        <KPITile testId="fin-alerts" label="Over threshold" value={summary.outstanding_alert_count || 0} sub={`${money(summary.outstanding_alert_threshold_usd || 5000)} alert`} />
        <KPITile tone="dark" testId="fin-invoices" label="Total Invoices" value={summary.invoices_total} sub={`${ay} · ${term}`} />
      </div>

      <div className="mb-4">
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="px-3 py-2 border border-slate-200 rounded-md text-sm bg-white" data-testid="finance-status-filter">
          <option value="">All statuses</option>
          <option value="paid">Paid</option>
          <option value="part_paid">Part paid</option>
          <option value="issued">Issued</option>
          <option value="overdue">Overdue</option>
        </select>
      </div>

      <Section>
        <DataTable
          testId="invoices-table"
          columns={[
            {
              key: "student", label: "Student", render: (r) => (
                r.student_name ? (
                  <div>
                    <div className="font-semibold text-slate-800">{r.student_name}</div>
                    {r.admission_no && <div className="text-xs text-slate-500 mono">{r.admission_no}</div>}
                  </div>
                ) : <span className="text-slate-400">N/A</span>
              ),
            },
            { key: "year", label: "Year Group", render: (r) => r.year_group || <span className="text-slate-400">N/A</span> },
            { key: "total", label: "Total", render: (r) => <span className="mono">{money(r.total)}</span> },
            { key: "amount_paid", label: "Paid", render: (r) => <span className="mono">{money(r.amount_paid)}</span> },
            { key: "due_date", label: "Due" },
            { key: "status", label: "Status", render: (r) => <StatusPill status={r.status} /> },
          ]}
          rows={filtered.slice(0, 200)}
        />
      </Section>
    </div>
  );
}
